import { forwardRef, useEffect, useImperativeHandle, useRef, type CSSProperties, type ReactNode } from 'react'
import type { BattleSnapshot, Hit } from '@boss/shared'
import { ParticleCanvas, type ParticleHandle } from '../fx/ParticleCanvas'
import { allImages, impactFor, scene, stageIndex } from './config'

export interface HitFx {
  /** Which goal (head) the opening belongs to, in goal order. */
  goalIndex: number
  /** Points scored: 1, 2 or 4. Drives the floating number and the burst size. */
  points: number
  superEffective: boolean
  crit: boolean
  /** The frame is about to swap, so hit harder. */
  stageChanged: boolean
  /** This opening completed its goal. */
  goalCompleted: boolean
}

export interface SceneHandle {
  hit(hit: Hit, fx: HitFx): void
  victory(): void
  reset(): void
}

interface Props {
  snapshot: BattleSnapshot | null
  /** Rendered inside the frame's box, above the artwork: the battle box lives here on wide screens. */
  overlay?: ReactNode
  /** Defeat screen lines. */
  victory?: { eyebrow: string; title: string; subtitle: string }
  /** Rendered in the frame's bottom-right corner: the big "How to play" prompt. */
  corner?: ReactNode
}

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * The whole frame is one of N full-screen images picked by HP. Hits shake and
 * flash the current frame and spawn sparks at the pack's impact point; a stage
 * change crossfades to the next frame with a heavier shake.
 *
 * `.viewport` is the box the frame is cropped into: the whole arena on desktop,
 * a band between the HUD and the feed on portrait phones (see styles.css).
 */
export const Scene = forwardRef<SceneHandle, Props>(function Scene({ snapshot, overlay, victory, corner }, ref) {
  const root = useRef<HTMLDivElement>(null)
  const dmgLayer = useRef<HTMLDivElement>(null)
  const flash = useRef<HTMLDivElement>(null)
  const particles = useRef<ParticleHandle>(null)

  // Preload every frame so a stage change never flashes an empty screen.
  useEffect(() => {
    for (const src of allImages()) {
      const img = new Image()
      img.src = src
    }
  }, [])

  const restart = (cls: string) => {
    const node = root.current
    if (!node || reduceMotion()) return
    node.classList.remove('hit', 'stagehit')
    void node.offsetWidth // reflow so the animation restarts
    node.classList.add(cls)
    node.addEventListener('animationend', () => node.classList.remove(cls), { once: true })
  }

  const spawnNumber = (at: { x: number; y: number }, text: string, cls = '') => {
    const layer = dmgLayer.current
    if (!layer) return
    const el = document.createElement('div')
    el.className = `dmg ${cls}`
    el.textContent = text
    el.style.left = `${at.x * 100}%`
    el.style.top = `${at.y * 100}%`
    layer.appendChild(el)
    el.addEventListener('animationend', () => el.remove(), { once: true })
  }

  useImperativeHandle(ref, () => ({
    hit(_hit, { goalIndex, points, superEffective, crit, stageChanged, goalCompleted }) {
      const at = impactFor(goalIndex)
      const bonus = superEffective || crit
      const heavy = stageChanged || goalCompleted || bonus
      restart(heavy ? 'stagehit' : 'hit')
      spawnNumber(at, `-${points}`, `${heavy ? 'big' : ''}${crit ? ' crit' : ''}`)
      // Callouts stack upward and stagger, so a combined hit shows both lines clearly.
      let delay = 220
      let row = 0
      const callout = (text: string, cls: string) => {
        const y = at.y - 0.08 - row * 0.07
        window.setTimeout(() => spawnNumber({ x: at.x, y }, text, cls), delay)
        delay += 450
        row += 1
      }
      if (superEffective) callout("It's super effective!", 'big callout super')
      if (crit) callout('A critical hit!', 'big callout crit')
      if (goalCompleted) callout('Goal reached', 'big callout goal')
      if (reduceMotion()) return
      const count = 24 * Math.max(1, points) + (heavy ? 30 : 0)
      const colors = crit ? ['#ffd166', '#ffffff', '#ff3b30', '#ff9f1c'] : superEffective ? ['#7cffc4', '#ffffff', '#3dbe6c', '#c9ffe9'] : undefined
      particles.current?.burst(at.x, at.y, { count, speed: heavy ? 1.3 : 1, colors })
      if (heavy) particles.current?.burst(at.x, at.y + 0.05, { count: 24, shape: 'dust', colors: ['#c9d2ee', '#8fa0d6'], speed: 0.5 })
    },
    victory() {
      const f = flash.current
      if (f) {
        f.classList.remove('go')
        void f.offsetWidth
        f.classList.add('go')
      }
      restart('stagehit')
      if (!reduceMotion()) window.setTimeout(() => particles.current?.confetti(), 600)
    },
    reset() {
      particles.current?.clear()
      dmgLayer.current?.replaceChildren()
    },
  }))

  const defeated = snapshot?.defeated ?? false
  const stage = snapshot ? stageIndex(snapshot.progress) : 0
  const style = {
    '--ar': String(scene.canvas.w / scene.canvas.h),
    '--fx': String(scene.focus?.x ?? 0.5),
    '--fy': String(scene.focus?.y ?? 0.5),
    '--mcrop': String(scene.mobile?.crop ?? 1),
    '--mfx': String(scene.mobile?.focusX ?? scene.focus?.x ?? 0.5),
    '--cw': String(scene.canvas.w),
    '--max-scale': String(scene.desktop?.maxScale ?? 1),
  } as CSSProperties

  return (
    <div className="viewport" style={style}>
      <div ref={root} className={`scene${defeated ? ' defeated' : ''}`} aria-label="Boss battle scene" data-stage={defeated ? 'final' : stage}>
        {scene.stages.map((src, i) => (
          <img key={src} src={src} alt="" draggable={false} className={`layer frame${!defeated && i === stage ? ' on' : ''}`} />
        ))}
        <img src={scene.final} alt="The boss has been defeated" draggable={false} className={`layer frame final${defeated ? ' on' : ''}`} />
        <ParticleCanvas ref={particles} />
        <div className="dmg-layer" ref={dmgLayer} aria-live="off" />
        <div className="white-flash" ref={flash} aria-hidden="true" />
      </div>
      {overlay}
      {corner}
      <div className={`victory-banner${defeated ? ' show' : ''}`} aria-hidden={!defeated}>
        <span className="victory-eyebrow">{victory?.eyebrow ?? 'Raid complete'}</span>
        <span className="victory-title">{victory?.title ?? 'Victory'}</span>
        <span className="victory-subtitle">{victory?.subtitle ?? 'Boss felled'}</span>
      </div>
    </div>
  )
})
